import React, { useEffect, useState } from 'react'
import qs from 'query-string'
import {
  BlockingLoader,
  Button,
  Container,
  CheckboxGroup,
  Icon,
  Link,
  Popup,
  TextBody,
  TextCaption,
  TextTitle,
  RadioGroup,
  VariantBadge
} from '@pmi/pmi-iqos-ds-react'

import { htmlTranslation, canUseDOM } from '@pmi/pmi-iqos-shared-react'
import { ZynAnalytics } from '@zyn-common/services'

const FILTER_TYPE = {
  NFS: 'NFS',
  NFM: 'NFM',
  RFM: 'RFM'
}

const SORT_TYPE = {
  LATEST_RELEASE_ASC: 'LATEST_RELEASE_ASC',
  PRICE_ASC: 'PRICE_ASC',
  PRICE_DESC: 'PRICE_DESC'
}

const SORT_OPTIONS_TK_MAP = {
  [SORT_TYPE.LATEST_RELEASE_ASC]: 'tk_sortLatestReleaseAsc',
  [SORT_TYPE.PRICE_ASC]: 'tk_sortPriceAsc',
  [SORT_TYPE.PRICE_DESC]: 'tk_sortPriceDesc'
}

const ProductListFilters = ({
  translations,
  filterOptions,
  sortOptions,
  originalList = [],
  setListFiltered
}) => {
  const [isFiltersReady, setIsFiltersReady] = useState(false)
  const [isSortingReady, setIsSortingReady] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({}) // { [filter.id]: [option.id, ...] }
  const [disabledFilters, setDisabledFilters] = useState({}) // { [filter.id]: [option.id, ...] }
  const [selectedSort, setSelectedSort] = useState('') // Sort option id
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [selectedFiltersCopy, setSelectedFiltersCopy] = useState({})
  const [selectedSortCopy, setSelectedSortCopy] = useState('')
  const [popstateChanged, setPopstateChanged] = useState(false)

  useEffect(() => {
    const listenPopstateEvent = (kill = false) => {
      const e = 'popstate'
      const f = () => {
        setPopstateChanged(true)
      }
      if (kill) {
        window.removeEventListener(e, f)
      } else {
        window.addEventListener(e, f)
      }
    }
    listenPopstateEvent()
    return () => {
      listenPopstateEvent(true)
    }
  }, [])

  useEffect(() => {
    // Get new URL params and update filters accordingly
    if (popstateChanged) {
      const selectedFiltersParams = getFiltersFromUrlParams()
      const res = updateDisabledFilters(selectedFiltersParams)
      setSelectedFilters(res.filtersSelected)
      setDisabledFilters(res.filtersDisabled)
      setTimeout(() => {
        setPopstateChanged(false)
      }, 0)
    }
  }, [popstateChanged])

  useEffect(() => {
    // Initial load
    if (!isFiltersReady && filterOptions) {
      if (filterOptions.length) {
        const selectedFiltersParams = getFiltersFromUrlParams()
        // Get the disabled filters from initial load
        const res = updateDisabledFilters(selectedFiltersParams)
        setSelectedFilters(res.filtersSelected)
        setDisabledFilters(res.filtersDisabled)
      }
      setIsFiltersReady(true)
    }
  }, [filterOptions])

  useEffect(() => {
    if (!isSortingReady && sortOptions) {
      if (sortOptions.length) {
        // Sort with the firt sort option from initial load
        setSelectedSort(sortOptions[0].id)
      }
      setIsSortingReady(true)
    }
  }, [sortOptions])

  useEffect(() => {
    if (selectedSort) {
      // Get current filtered and sorted list
      const list = sortList(getListToPresent(selectedFilters))
      // Trigger to present it
      setListFiltered(list)
    }
  }, [selectedSort])

  useEffect(() => {
    if (isFiltersReady) {
      const res = updateDisabledFilters(selectedFilters)
      // Store new filters state
      setSelectedFilters(res.filtersSelected)
      setDisabledFilters(res.filtersDisabled)
      // Sort the new list
      const newListFiltered = getListToPresent(res.filtersSelected)
      const list = sortList(newListFiltered)
      // Trigger to present the new list
      setListFiltered(list)
      // Update URL params with new selected filters
      if (!isPopupOpen && !popstateChanged) {
        updateUrlParams(res.filtersSelected)
      }
    }
  }, [selectedFilters])

  const updateUrlParams = filtersSelection => {
    // Get current params
    const params = qs.parse(window.location.search, {
      arrayFormat: 'comma'
    })
    let hasChanges = false
    filterOptions.forEach(filter => {
      // If filter is selected
      if (filtersSelection[filter.id]) {
        const newFilterOptions = filtersSelection[filter.id].map(
          id => filter.options.find(o => o.id === id).attribute
        )
        const oldFilterOptions = Array.isArray(params[filter.attribute])
          ? params[filter.attribute]
          : [params[filter.attribute]]
        newFilterOptions.sort()
        if (
          !(
            oldFilterOptions.length === newFilterOptions.length &&
            oldFilterOptions.sort().every((v, i) => {
              return v === newFilterOptions[i]
            })
          )
        ) {
          params[filter.attribute] = newFilterOptions
          hasChanges = true
        }
        // If is not selected but exists in params
      } else if (params[filter.attribute]) {
        delete params[filter.attribute]
        hasChanges = true
      }
    })
    // If no changes we dont push a new state
    if (hasChanges) {
      let newUrl
      if (Object.keys(params).length) {
        newUrl =
          window.location.pathname +
          '?' +
          qs.stringify(params, {
            arrayFormat: 'comma'
          })
      } else {
        newUrl = window.location.pathname
      }
      window.history.pushState({}, '', newUrl)
    }
  }

  const getFiltersFromUrlParams = () => {
    // Get query string params filters selection
    const params = qs.parse(window.location.search, {
      arrayFormat: 'comma'
    })
    const selectedFiltersParams = {}
    Object.keys(params).forEach(param => {
      filterOptions.forEach(filter => {
        if (filter.attribute === param) {
          const selectedOptionsAttributes = Array.isArray(params[param])
            ? params[param]
            : [params[param]]
          const optionsIds = filter.options
            .filter(o => selectedOptionsAttributes.includes(o.attribute))
            .map(o => o.id)
          if (optionsIds.length) {
            if (filter.type === FILTER_TYPE.NFS) {
              selectedFiltersParams[filter.id] = [optionsIds[0]]
            } else {
              selectedFiltersParams[filter.id] = optionsIds
            }
          }
        }
      })
    })
    return selectedFiltersParams
  }

  const sortList = (list = []) => {
    if (!list.length) return []
    let sortedList = [...list]
    switch (selectedSort) {
      case SORT_TYPE.LATEST_RELEASE_ASC:
        sortedList.sort((a, b) => b.releaseDate - a.releaseDate)
        break
      case SORT_TYPE.PRICE_ASC:
        // Product list - find the option with lowest price
        if (sortedList[0].options) {
          sortedList = sortedList.map(p => {
            p.lowestPrice = p.options[0].price || 0
            p.options.forEach((o, i) => {
              if (o.price < p.lowestPrice) {
                p.lowestPrice = o.price
                p.selectedOptionIndex = i
              }
            })
            return { ...p }
          })
          sortedList.sort((a, b) => a.lowestPrice - b.lowestPrice)
        } else {
          // Bundle list
          sortedList.sort((a, b) => a.price - b.price)
        }
        break
      case SORT_TYPE.PRICE_DESC:
        // Product list - find the option with highest price
        if (sortedList[0].options) {
          sortedList = sortedList.map(p => {
            p.highestPrice = p.options[0].price || 0
            p.options.forEach((o, i) => {
              if (o.price > p.highestPrice) {
                p.highestPrice = o.price
                p.selectedOptionIndex = i
              }
            })
            return { ...p }
          })
          sortedList.sort((a, b) => b.highestPrice - a.highestPrice)
        } else {
          // Bundle list
          sortedList.sort((a, b) => b.price - a.price)
        }
        break
    }
    return sortedList
  }

  const handleFilterOptionClick = (filter, option) => {
    ZynAnalytics.productListFilterSelect(option.name)
    setSelectedFilters(getNewSelectedFilters(filter, option))
  }

  const getNewSelectedFilters = (filter, option) => {
    const newSelectedFilters = { ...selectedFilters }
    // All or Unspecified
    if (option.id === '0') {
      delete newSelectedFilters[filter.id]
    } else {
      if (newSelectedFilters[filter.id]) {
        const optionIndex = newSelectedFilters[filter.id].indexOf(option.id)
        if (optionIndex === -1) {
          if (filter.type === FILTER_TYPE.NFS) {
            newSelectedFilters[filter.id] = [option.id]
          } else {
            newSelectedFilters[filter.id].push(option.id)
          }
        } else {
          newSelectedFilters[filter.id].splice(optionIndex, 1)
          !newSelectedFilters[filter.id].length &&
            delete newSelectedFilters[filter.id]
        }
      } else {
        newSelectedFilters[filter.id] = [option.id]
      }
    }
    return newSelectedFilters
  }

  const getListToPresent = filtersSelected => {
    // If no filter is selected, we return the full list, otherwise we continue
    if (!Object.keys(filtersSelected).length) {
      return originalList.map(el => ({ ...el }))
    }
    let listToFilter = originalList.map(el => ({ ...el }))
    let matchedItems = []
    // Iterate each selected filter
    Object.keys(filtersSelected).forEach((filter, i) => {
      // If some filter matched from previus iteration (apply AND operator)
      if (matchedItems.length) {
        listToFilter = [
          ...listToFilter.filter(el =>
            matchedItems.map(mEl => mEl.id).includes(el.id)
          )
        ]
        // Clean up for next filter iteration
        matchedItems = []
      } else if (i) {
        return
      }
      // Iterate each item from the list of items to be filtered
      listToFilter.forEach(el => {
        let match = false
        let selectedOptionIndex = null
        // Check if any selected option matchs with the attribute (OR operator)

        filtersSelected[filter].sort().forEach(selectedFilterOption => {
          match = match || el.optionsAttributes.includes(selectedFilterOption)
          const option =
            el.options &&
            el.options.find(
              i => i.attributes.indexOf(selectedFilterOption) > -1
            )

          const idx = el.options && el.options.indexOf(option)

          if (selectedOptionIndex === null && match) {
            selectedOptionIndex = idx > -1 ? idx : 0
          }
        })
        // If match add the item to the list to be filtered for the next filter iteration

        const { variantSelector } = filterOptions.find(i => i.id === filter)

        match &&
          matchedItems.push({
            ...el,
            ...(variantSelector && { selectedOptionIndex })
          })
      })
    })

    return matchedItems
  }

  const updateDisabledFilters = filtersSelected => {
    const filtersDisabled = {}
    // Get all disabled filters (not possible to select)
    filterOptions.forEach(filter => {
      filter.options.forEach(option => {
        // Check if the option has results combined with the other selected filters
        const tempOptionSelection = {
          ...filtersSelected,
          [filter.id]: [option.id]
        }
        const matchResTemp = getListToPresent(tempOptionSelection)
        if (!matchResTemp.length) {
          // Store the disabled filter option
          if (filtersDisabled[filter.id]) {
            filtersDisabled[filter.id].push(option.id)
          } else {
            filtersDisabled[filter.id] = [option.id]
          }
          // If the disabled option is previuslly selected we need to unselect it
          if (filtersSelected[filter.id]) {
            const optionIndex = filtersSelected[filter.id].indexOf(option.id)
            if (optionIndex !== -1) {
              filtersSelected[filter.id].splice(optionIndex, 1)
              !filtersSelected[filter.id].length &&
                delete filtersSelected[filter.id]
            }
          }
        }
      })
    })
    return { filtersDisabled, filtersSelected }
  }

  const handleRemoveFilters = () => {
    // Clear selected filters object
    setSelectedFilters({})
    // Restore sort to default first option
    sortOptions && sortOptions.length && setSelectedSort(sortOptions[0].id)
  }

  const renderFilterOptionColor = option => {
    if (option.value)
      return (
        <Container pr="1">
          <VariantBadge
            color={option.value}
            size={VariantBadge.sizes.s}
            disabled
          />
        </Container>
      )
    return null
  }

  const getCheckboxGroupCommonProps = filter => ({
    name: filter.attribute,
    label: '',
    getItemLabel: option => (
      <Container
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.center}>
        {renderFilterOptionColor(option)}
        <TextBody
          size={TextBody.sizes.s}
          value={
            /** To help only for tests */
            canUseDOM && window.filters_debug
              ? option.name + ' (' + option.id + ')'
              : option.name
          }
        />
      </Container>
    ),
    onChangeHandler: option => handleFilterOptionClick(filter, option),
    checkDesign: CheckboxGroup.checkDesigns.button,
    toggleDesign: CheckboxGroup.toggleDesigns.square,
    isItemDisabled: option => disabledFilters[filter.id]?.includes(option.id)
  })

  const renderFiltersSection = () => {
    const hasFilters = filterOptions && filterOptions.length
    const hasSorts = sortOptions && sortOptions.length
    if (!hasFilters && !hasSorts) {
      return (
        <TextBody
          size={TextBody.sizes.s}
          {...htmlTranslation(translations, 'tk_noFiltersAvailable')}
        />
      )
    }
    return (
      <Container>
        {filterOptions && filterOptions.length
          ? filterOptions.map(filter => (
              <Container key={filter.id} fullWidth pt="10">
                <Container pb="2">
                  <TextCaption size={TextCaption.sizes.m}>
                    {filter.name}
                  </TextCaption>
                </Container>
                <Container pb="4">
                  <TextBody
                    size={TextBody.sizes.s}
                    value={filter.description}
                  />
                </Container>
                {filter.type === FILTER_TYPE.RFM ? (
                  <Container pb="2">
                    <CheckboxGroup
                      {...getCheckboxGroupCommonProps(filter)}
                      items={[
                        {
                          id: '0',
                          name: translations.tk_filtersUnspecified
                        }
                      ]}
                      isItemSelected={option =>
                        !selectedFilters[filter.id] && option.id === '0'
                      }
                    />
                  </Container>
                ) : null}
                <CheckboxGroup
                  {...getCheckboxGroupCommonProps(filter)}
                  items={getFiltersItems(filter)}
                  isItemSelected={option => {
                    if (!selectedFilters[filter.id] && option.id === '0')
                      return true
                    return !!selectedFilters[filter.id]?.includes(option.id)
                  }}
                  columns={
                    filter.type === FILTER_TYPE.RFM
                      ? getFiltersItems(filter).length
                      : 0
                  }
                  fullWidthToggle={filter.type === FILTER_TYPE.RFM}
                />
                {filter.type === FILTER_TYPE.RFM ? (
                  <Container
                    fullWidth
                    display={Container.displays.flex}
                    justifyContent={Container.justifyContentValues.between}
                    pt="2">
                    <TextBody
                      size={TextBody.sizes.s}
                      value={translations.tk_filterLow}
                    />
                    <TextBody
                      size={TextBody.sizes.s}
                      value={translations.tk_filterHigh}
                    />
                  </Container>
                ) : null}
              </Container>
            ))
          : null}
        {sortOptions && sortOptions.length ? (
          <Container pt="10">
            <TextCaption size={TextCaption.sizes.m}>
              {translations.tk_sort}
            </TextCaption>
            <RadioGroup
              name="sortOptions"
              label=""
              columns={1}
              items={sortOptions}
              getItemValue={option => option.id}
              getItemContent={option => (
                <TextBody
                  size={TextBody.sizes.m}
                  value={translations[SORT_OPTIONS_TK_MAP[option.id]]}
                />
              )}
              isItemSelected={option => selectedSort === option.id}
              onChangeHandler={option => {
                setSelectedSort(option.target.value)
              }}
            />
          </Container>
        ) : null}
        <Container pt="10">
          <Link onClickHandler={handleRemoveFilters}>
            {translations.tk_removeFilters}
          </Link>
        </Container>
      </Container>
    )
  }

  const getFiltersItems = filter => {
    const allFilters = [...filter.options]
    if (filter.type !== FILTER_TYPE.RFM) {
      allFilters.unshift({
        id: '0',
        name: translations.tk_filtersAll
      })
    }
    return allFilters
  }

  const renderSelectedFiltersButtons = () => {
    const selected = []
    filterOptions &&
      filterOptions.forEach(f => {
        f.options.forEach(o => {
          if (selectedFilters[f.id]?.includes(o.id)) {
            selected.push(
              <Container key={o.id} pt="2" pr="2">
                <Button
                  text={o.name}
                  icon={Icon.images.cancel}
                  height={Button.heights.s}
                  onClickHandler={() => {
                    handleFilterOptionClick(f, o)
                  }}
                />
              </Container>
            )
          }
        })
      })
    return selected
  }

  return (
    <>
      <Container
        display={[
          Container.displays.none,
          Container.displays.none,
          Container.displays.block
        ]}>
        {isFiltersReady && isSortingReady ? (
          <Container>
            <TextTitle
              heading={TextTitle.headings.h6}
              additionalClasses="q-text-turquoise">
              {translations.tk_filters}
            </TextTitle>
            {renderFiltersSection()}
          </Container>
        ) : (
          <Container fullWidth p="10" position={Container.positions.relative}>
            <BlockingLoader loading />
          </Container>
        )}
      </Container>
      <Container
        display={[
          Container.displays.block,
          Container.displays.block,
          Container.displays.none
        ]}>
        <Container
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.column}>
          <Container>
            <Button
              text={translations.tk_openFiltersPopupBtn}
              variant={Button.variants.secondary}
              onClickHandler={() => {
                // When opening the popup, we store a copy of the state to restore it
                // in case of the user does not apply the changes
                const fullselectedFiltersCopy = {}
                Object.keys(selectedFilters).forEach(key => {
                  fullselectedFiltersCopy[key] = [...selectedFilters[key]]
                })
                setSelectedFiltersCopy(fullselectedFiltersCopy)
                setSelectedSortCopy(selectedSort)
                setIsPopupOpen(true)
              }}
              loading={!isFiltersReady || !isSortingReady}
              disabled={!isFiltersReady || !isSortingReady}
            />
          </Container>
          <Container display={Container.displays.flex} flexWrap>
            {renderSelectedFiltersButtons()}
          </Container>
        </Container>
        <Popup
          isOpen={isPopupOpen}
          fullScreen
          onCloseHandler={() => {
            // Restore to previus state before opening the popup
            setSelectedFilters({ ...selectedFiltersCopy })
            setSelectedSort(selectedSortCopy)
            setIsPopupOpen(false)
          }}>
          <Container fullWidth>
            <Container divider={Container.dividers.bottom} pb="10">
              <TextTitle heading={TextTitle.headings.h5}>
                {translations.tk_filters}
              </TextTitle>
            </Container>
            {renderFiltersSection()}
            <Container fullWidth pt="10" pb="32">
              <Button
                fullWidth
                text={translations.tk_applyFiltersBtn}
                onClickHandler={() => {
                  setSelectedFiltersCopy({})
                  setSelectedSortCopy('')
                  setIsPopupOpen(false)
                  updateUrlParams(selectedFilters)
                }}
              />
            </Container>
          </Container>
        </Popup>
      </Container>
    </>
  )
}

export default ProductListFilters
